// ---------------------------------------------------------------------------
// API ฝั่งเนื้อหา/ค้นพบ/โซเชียล/โปรไฟล์/แอดมิน (เฟส 2) — เรียก GS ตาม docs/API.md โดยตรงผ่าน gsPost
//  - IS_MOCK: ใช้ mock-content.js (action ชื่อเดียวกัน คืนรูปแบบเดียวกับ GS) ไม่ยิงเน็ต
//  - ฟังก์ชันคืน "รูปแบบของหน้าเว็บ": การ์ดตัวละครมี tags (ชื่อแท็ก) + creator แปลงจาก tagIds/createdBy ให้แล้ว
//  - ห้ามเรียกการ์ด NC (เว็บนี้ไม่มีเนื้อหา 18+) — กรองทิ้งฝั่ง client อีกชั้น
// ---------------------------------------------------------------------------
import { IS_MOCK, IS_FB } from "./config";
import { gsPost } from "./gsClient";
import { runContentMock } from "./mock-content";

const call = (action, params = {}) => (IS_MOCK ? runContentMock(action, params) : gsPost(action, params));

// ---- แท็ก (cache) -----------------------------------------------------------
const ADULT_TAG = /^(nc|nc-?17|18\+|r18|nsfw|นอกกาย|นอกใจ)$/i;
let tagPromise = null;
export const loadTags = () => {
  if (!tagPromise) {
    tagPromise = call("tags.list")
      .then((r) => (r?.items || []).filter((t) => t.isActive !== false && !ADULT_TAG.test(t.name) && !ADULT_TAG.test(t.id)))
      .catch((e) => {
        tagPromise = null;
        throw e;
      });
  }
  return tagPromise;
};
const tagNames = (ids, tags) => (ids || []).map((id) => tags.find((t) => t.id === id)?.name).filter(Boolean);
const tagIdsOf = (names, tags) => (names || []).map((n) => tags.find((t) => t.name === n || t.id === n)?.id).filter(Boolean);

export const adaptCard = (c, tags = []) => ({
  ...c,
  tags: tagNames(c.tagIds, tags),
  creator: c.createdBy ? { id: c.createdBy, username: c.creatorUsername || "", displayName: c.creatorName || "" } : undefined,
  creatorId: c.createdBy,
});
const safeCards = (items, tags) => (items || []).filter((c) => c.contentRating !== "NC").map((c) => adaptCard(c, tags));

// ---- รูปภาพ: ย่อก่อนอัปโหลด (≤5MB) --------------------------------------------------
export const MAX_UPLOAD = 5 * 1024 * 1024;
const INLINE_LIMIT = 150 * 1024; // โหมด Firebase: รูปเก็บในฐานข้อมูลโดยตรง (ฝั่ง fb/content.js ยอมสูงสุด 200KB)
const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("เปิดไฟล์รูปไม่สำเร็จ"));
    };
    img.src = url;
  });
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = () => reject(new Error("อ่านไฟล์ไม่สำเร็จ"));
    r.readAsDataURL(file);
  });

/** ตรวจ + ย่อรูป -> { mime, data(base64 ไม่มี prefix), name } ; GIF ไม่ย่อ (เพื่อคงภาพเคลื่อนไหว) */
export async function prepareImage(file, maxSide) {
  const type = (file.type || "").replace("image/", "");
  if (!["jpeg", "png", "gif", "webp"].includes(type)) throw new Error("รองรับเฉพาะ JPG / PNG / GIF / WEBP");
  if (type === "gif") {
    if (file.size > (IS_FB ? INLINE_LIMIT : MAX_UPLOAD)) throw new Error(IS_FB ? "ไฟล์ GIF ใหญ่เกิน 150KB" : "ไฟล์ GIF ใหญ่เกิน 5MB");
    return { mime: "gif", data: await fileToBase64(file), name: file.name };
  }
  // โหมด Firebase เก็บรูปเป็น data URL ในฐานข้อมูลโดยตรง -> ย่อให้เล็ก (≤ INLINE_LIMIT)
  const cap = IS_FB ? INLINE_LIMIT : MAX_UPLOAD;
  let limit = maxSide || (IS_MOCK || IS_FB ? 640 : 1280);
  const img = await loadImage(file);
  const draw = (side) => {
    const scale = Math.min(1, side / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  };
  let q = IS_MOCK || IS_FB ? 0.7 : 0.85;
  let canvas = draw(limit);
  let data = canvas.toDataURL("image/jpeg", q).split(",")[1] || "";
  for (let i = 0; i < 8 && data.length * 0.75 > cap; i++) {
    if (q > 0.45) q -= 0.15;
    else if (IS_FB) {
      limit = Math.round(limit * 0.8);
      canvas = draw(limit);
    } else break;
    data = canvas.toDataURL("image/jpeg", q).split(",")[1] || "";
  }
  if (data.length * 0.75 > cap) throw new Error(IS_FB ? "รูปใหญ่เกินไป แม้ย่อแล้ว ลองรูปอื่น" : "รูปใหญ่เกิน 5MB แม้ย่อแล้ว ลองรูปอื่น");
  return { mime: "jpeg", data, name: file.name.replace(/\.[^.]+$/, "") + ".jpg" };
}

// ---- รหัสแชร์ตัวละคร --------------------------------------------------------------
/** โค้ดแชร์ 7 ตัวจาก GS (character.shareCode) ; ถ้ายังไม่มีใช้ id เดิมเป็นค่าสำรอง */
export const shareCode = (character) => (typeof character === "object" && character ? character.shareCode : "") || String(character?.id || character || "").replace(/^(ch|c)_?/, "");
export const shareUrl = (id) => `${window.location.origin}/c/${id}`;
const CODE_PREFIXES = ["", "ch", "ch_", "c", "c_"];

export const content = {
  tags: { list: loadTags },

  upload: async (file, type = "character", maxSide) => {
    const { mime, data, name } = await prepareImage(file, maxSide);
    const r = await call("upload", { type, mime, name, data });
    if (!r?.url) throw new Error("อัปโหลดไม่สำเร็จ");
    return r;
  },

  characters: {
    list: async ({ tags, ...p } = {}) => {
      const all = await loadTags();
      const r = await call("characters.list", { ...p, ...(tags?.length ? { tags: tagIdsOf(tags, all).join(",") } : {}) });
      return { ...r, items: safeCards(r.items, all) };
    },
    get: async (id) => {
      const all = await loadTags();
      const c = await call("characters.get", { id });
      if (c?.contentRating === "NC") throw new Error("ตัวละครนี้ไม่พร้อมแสดงบนเว็บนี้");
      return adaptCard(c, all);
    },
    mine: async () => {
      const all = await loadTags();
      const r = await call("characters.mine", {});
      return (r.items || []).map((c) => adaptCard(c, all));
    },
    /** data = รูปแบบฟอร์ม (tags เป็นชื่อแท็ก) ; draft=true บันทึกร่าง */
    save: async ({ tags, ...data }) => {
      const all = await loadTags();
      const r = await call("characters.save", { ...data, tagIds: tagIdsOf(tags, all), contentRating: "PG" });
      return adaptCard(r.character, all);
    },
    archive: (id, archived) => call("characters.archive", { id, archived }),
    like: (id) => call("like.toggle", { targetType: "character", targetId: id }),
    toggleSave: (id) => call("like.toggle", { targetType: "save", targetId: id }),
    aiDraft: async (idea, language) => {
      const r = await call("ai.characterDraft", { idea, ...(language ? { language } : {}) });
      const all = await loadTags();
      return { ...r.draft, tags: tagNames(r.draft?.tagIds, all), mock: r.mock, simple: r.simple };
    },
    aiSearch: async (q) => {
      const all = await loadTags();
      const r = await call("ai.search", { q });
      return { ...r, items: safeCards(r.items, all), interpreted: { ...r.interpreted, tags: tagNames(r.interpreted?.tagIds, all) } };
    },
    /** ค้นด้วยโค้ดแชร์ — ลองเป็น id ตรง ๆ และ id ที่มี prefix ; ไม่พบ = null */
    byCode: async (code) => {
      const clean = String(code || "").trim();
      if (!/^[A-Za-z0-9_-]{2,40}$/.test(clean)) return null;
      if (/^[A-Za-z2-9]{7}$/.test(clean)) {
        try {
          const all = await loadTags();
          const r = await call("characters.byCode", { code: clean.toUpperCase() });
          return adaptCard(r.character, all);
        } catch (e) {
          if (e.code === "unauthorized") throw e;
        }
      }
      for (const p of CODE_PREFIXES) {
        try {
          return await content.characters.get(`${p}${clean}`);
        } catch (e) {
          if (e.code === "unauthorized") throw e;
        }
      }
      return null;
    },
    /** ตัวละครที่ไลก์/บันทึกไว้ (targetType = character | save) */
    marked: async (targetType, limit = 24) => {
      const r = await call("likes.mine", { targetType, limit });
      const ids = (r.items || []).map((x) => x.targetId).slice(0, limit);
      const rows = await Promise.all(ids.map((id) => content.characters.get(id).catch(() => null)));
      return rows.filter(Boolean);
    },
    /** อันดับสายเปย์รายตัวละคร (GS gifts.ranking, cache ฝั่ง server) -> items [{rank, name, photoURL, coins}] */
    giftRanking: async (characterId, period = "month") => {
      const r = await call("gifts.ranking", { characterId, period });
      return { ...r, items: (r.items || []).map((x) => ({ ...x, name: x.displayName || x.username || "ผู้ใช้", userId: x.userId })) };
    },
  },

  universes: {
    list: async (p = {}) => {
      const r = await call("universes.list", p);
      return { ...r, items: (r.items || []).map((u) => ({ ...u, creator: u.createdBy ? { id: u.createdBy, displayName: u.creatorName, username: u.creatorName } : undefined })) };
    },
    get: async (id) => {
      const all = await loadTags();
      const r = await call("universes.get", { id });
      const u = r.universe || {};
      return { ...u, tags: tagNames(u.tagIds, all), creator: { id: u.createdBy, displayName: u.creatorName, username: u.creatorName }, characters: safeCards(r.characters, all) };
    },
    save: async ({ tags, ...data }) => {
      const all = await loadTags();
      return (await call("universes.save", { ...data, tagIds: tagIdsOf(tags, all) })).universe;
    },
    /** เริ่มแชทจักรวาล (chat.create universeId) ; mock: เปิดแชทกับตัวละครแรกแทน */
    startChat: async (universeId, firstCharacterId, createCharChat) => {
      if (IS_MOCK) return createCharChat(firstCharacterId);
      const r = await gsPost("chat.create", { universeId });
      return r.chat;
    },
  },

  creators: {
    list: (p = {}) => call("creators.list", { sort: "followers", limit: 30, ...p }),
    /** :id ของเส้นทางอาจเป็น userId หรือ username — ลอง userId ก่อน */
    profile: async (idOrUsername) => {
      const all = await loadTags();
      let r;
      try {
        r = await call("profile.get", { userId: idOrUsername });
      } catch (e) {
        if (e.code === "unauthorized") throw e;
        r = await call("profile.get", { username: idOrUsername });
      }
      return { ...r.user, isFollowing: r.isFollowing, characters: safeCards(r.characters, all) };
    },
    follow: (userId) => call("follow.toggle", { userId }),
  },

  /** รวมผลอันดับ day|week เป็นรูปแบบเดียวกันทั้งโหมดจริง/mock */
  rankings: async (period = "week") => {
    const r = await call("rankings.overview", { period });
    const norm = (g) => ({ chats: g?.chats || [], likes: g?.likes || [], gifts: g?.gifts || [] });
    return { period: r.period || period, since: r.since, characters: norm(r.characters), creators: norm(r.creators) };
  },

  comments: {
    list: (targetType, targetId, sort = "new") => call("comments.list", { targetType, targetId, sort, limit: 50 }).then((r) => r.items || []),
    add: (targetType, targetId, text, parentId) => call("comment.add", { targetType, targetId, text, ...(parentId ? { parentId } : {}) }).then((r) => r.comment),
    remove: (id) => call("comment.delete", { id }),
    like: (id) => call("like.toggle", { targetType: "comment", targetId: id }),
  },

  posts: {
    list: (p = {}) => call("posts.list", { limit: 20, ...p }),
    add: (p) => call("post.add", p).then((r) => r.post),
    remove: (id) => call("post.delete", { id }),
    like: (id) => call("like.toggle", { targetType: "post", targetId: id }),
  },

  like: (targetType, targetId) => call("like.toggle", { targetType, targetId }),
  report: (p) => call("report.create", p),

  personas: {
    list: () => call("personas.list", { scope: "mine" }).then((r) => r.items || []),
    save: (d) => call("personas.save", d).then((r) => r.persona),
  },
  scenarios: {
    list: () => call("scenarios.list", { scope: "mine" }).then((r) => r.items || []),
    save: (d) => call("scenarios.save", d).then((r) => r.scenario),
  },

  notifications: {
    list: (category) => call("notifications.list", { limit: 40, ...(category && category !== "all" ? { category } : {}) }),
    read: (id) => call("notifications.read", { id }),
    readAll: () => call("notifications.read", { all: true }),
  },

  me: {
    get: async () => {
      const r = await call("me.get", {});
      const u = r.user || {};
      return { ...u, bio: u.bio ?? u.description ?? "", socialLinks: u.socialLinks || [], preferences: u.preferences || {}, vip: r.vip };
    },
    update: (patch) => call("me.update", patch).then((r) => r.user),
    savePrefs: async (patch) => {
      const cur = (await content.me.get()).preferences;
      return content.me.update({ preferences: { ...cur, ...patch } });
    },
  },

  admin: {
    stats: (days = 14) => call("admin.stats", { days }),
    users: { list: (p = {}) => call("admin.users.list", { limit: 30, ...p }), get: (id) => call("admin.users.get", { id }), ban: (id, banned, reason) => call("admin.users.ban", { id, banned, reason }), adjustCoins: (id, amount, reason) => call("admin.users.adjustCoins", { id, amount, reason }) },
    characters: { list: (p = {}) => call("admin.characters.list", { limit: 30, ...p }), review: (id, decision, reason) => call("admin.characters.review", { id, decision, ...(reason ? { reason } : {}) }) },
    config: {
      list: (table) => call("admin.config.list", { table }).then((r) => r.items || []),
      save: (table, data) => call("admin.config.save", { table, data }).then((r) => r.item),
      remove: (table, id) => call("admin.config.remove", { table, id }).then((r) => r.item),
    },
    reports: { list: (p = {}) => call("admin.reports.list", { limit: 30, ...p }), resolve: (id, resolution) => call("admin.reports.resolve", { id, resolution }) },
    topups: { list: (p = {}) => call("admin.topups.list", { limit: 30, ...p }) },
    settings: { get: () => call("admin.settings.get", {}).then((r) => r.settings || {}), set: (key, value) => call("admin.settings.set", { key, value }).then((r) => r.settings || {}) },
    clearCache: () => call("admin.cache.clear", {}),
  },
};

export default content;
